import React, { useState, useEffect } from 'react';
import Pagination from '@mui/material/Pagination';
import EntityRow from './entityTable/EntityRow';
import CyberRow from './cyberTable/CyberRow';
import EntityColumns from './entityTable/EntityColumns';
import CyberColumns from './cyberTable/CyberColumns';
import WColumns from './commodityTable/WColumns';
import WRow from './commodityTable/WRow';

interface IExpandedMultiRecordProps {
  row: any;
  handleSubmit: any;
  type: string;
  setShowFullRecord: any;
  showFilters: boolean;
  cyberHeaders: string[];
}

interface ScreeningResult {
  'Name of Entity': string;
  'Entity Country': string;
  'List Name': string;
  Agency?: string;
  'Additional Remarks'?: string;
  'Date Updated on BITE Platform'?: string;
}

const WExpandedMultiRecord: React.FC<IExpandedMultiRecordProps> = ({
  showFilters,
  setShowFullRecord,
  type,
  row,
  cyberHeaders,
}) => {
  const [pageCount, setPageCount] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentItems, setCurrentItems] = useState<any[]>([]);
  const [hitsData, setHitsData] = useState<any>(row?.body?.hits?.hits);
  const [sortConfig, setSortConfig] = useState<{
    key: keyof ScreeningResult;
    direction: 'asc' | 'desc';
  }>({
    key: 'Name of Entity',
    direction: 'asc',
  });
  const resultsPerPage = 50;

  const handlePageChange = (e: any, new_page: number) => {
    setCurrentPage(new_page);
    const indexOfLastItem = new_page * resultsPerPage;
    const indexOfFirstItem = indexOfLastItem - resultsPerPage;

    setCurrentItems(hitsData.slice(indexOfFirstItem, indexOfLastItem));
  };

  useEffect(() => {
    const indexOfLastItem = currentPage * resultsPerPage;
    const indexOfFirstItem = indexOfLastItem - resultsPerPage;
    setCurrentItems(hitsData.slice(indexOfFirstItem, indexOfLastItem));
    const totalPages = Math.ceil(hitsData.length / resultsPerPage);
    if (totalPages >= 200) {
      setPageCount(199);
    } else if (totalPages <= 199) {
      setPageCount(totalPages);
    }
  }, [row, hitsData]);

  const fieldMap: Record<string, string> = {
    nameOfEntity: 'Name of Entity',
    nameOfCountry: 'Entity Country',
    listName: 'List Name',
    agency: 'Agency',
    addRemarks: 'Additional Remarks',
    dateUpdated: 'Date Updated on BITE Platform',
    productName: 'Product Name',
    hsCode: 'HS Code',
    sourceCountry: 'Source Country',
    'CVE ID': 'CVE ID',
    'Vendor Project': 'Vendor Project',
    Product: 'Product',
    'Vulnerability Name': 'Vulnerability Name',
    'Required Action': 'Required Action',
    'Market Name': 'Market Name',
    Description: 'Description',
    Website: 'Website',
    'Market Location': 'Market Location',
  };

  const compareValues = (
    aVal: string,
    bVal: string,
    direction: 'asc' | 'desc',
  ) => {
    const aIsEmpty = !aVal?.trim();
    const bIsEmpty = !bVal?.trim();

    if (aIsEmpty && !bIsEmpty) {
      return direction === 'asc' ? -1 : 1;
    }
    if (!aIsEmpty && bIsEmpty) {
      return direction === 'asc' ? 1 : -1;
    }
    if (aIsEmpty && bIsEmpty) {
      return 0;
    }

    return direction === 'asc'
      ? aVal.localeCompare(bVal)
      : bVal.localeCompare(aVal);
  };

  const sortData = (key: string) => {
    const typedKey = key as keyof ScreeningResult;
    const sortedData = [...hitsData];

    const field = fieldMap[key];
    if (!field) {
      return;
    }

    const direction = sortConfig.direction;
    sortedData.sort((a, b) =>
      compareValues(
        a._source?.[field] || '',
        b._source?.[field] || '',
        direction,
      ),
    );

    // Toggle sort direction
    const newDirection = direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key: typedKey, direction: newDirection });
    setHitsData(sortedData);
  };

  return (
    <div className='screening-results-container'>
      {type === 'Entities' && (
        <>
          <EntityColumns
            showFilters={showFilters}
            sortData={sortData}
            sortConfig={sortConfig}
          />
          <div className='screening-results-table-container'>
            {currentItems.map((hit: any, index: number) => (
              <EntityRow
                showFilters={showFilters}
                setShowFullRecord={setShowFullRecord}
                hit={hit}
                key={index}
              />
            ))}
          </div>
        </>
      )}
      {type === 'Products' && (
        <>
          <WColumns
            showFilters={showFilters}
            sortData={sortData}
            sortConfig={sortConfig}
          />
          <div className='screening-results-table-container'>
            {currentItems.map((hit: any, index: number) => (
              <WRow
                showFilters={showFilters}
                setShowFullRecord={setShowFullRecord}
                hit={hit}
                key={index}
              />
            ))}
          </div>
        </>
      )}
      {type === 'Cyber' && (
        <>
          <CyberColumns
            showFilters={showFilters}
            headers={cyberHeaders}
            sortData={sortData}
            sortConfig={sortConfig}
          />
          <div className='screening-results-table-container'>
            {currentItems.map((hit: any, index: number) => (
              <CyberRow
                showFilters={showFilters}
                setShowFullRecord={setShowFullRecord}
                hit={hit}
                key={index}
                tab={
                  cyberHeaders.includes('CVE ID')
                    ? 'CVE'
                    : cyberHeaders.includes('Market Name')
                    ? 'Markets'
                    : 'Controls'
                }
              />
            ))}
          </div>
        </>
      )}
      {hitsData.length && (
        <div className='screening-results-footer'>
          <div className='screening-results-pagination-text'>
            Page {currentPage} of {pageCount}
          </div>
          <div className='screening-results-footer-action-group'>
            <div className='hb-pagination'>
              <Pagination
                onChange={handlePageChange}
                size='large'
                count={pageCount}
                shape='rounded'
                variant='text'
                page={currentPage}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WExpandedMultiRecord;
